import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";

import WorkoutStore from "stores/WorkoutStore";
import WorkoutDetailsStore from "stores/WorkoutDetailsStore";

import Spinner from "components/Spinner";
import Video from "components/Video";
import NoInternetConnectionPlaceholder from "components/NoInternetConnectionPlaceholder";

import { NoInternetConnectionError, NotFoundError } from "services/api";

import { redirectToRootRoute } from "utils/route";

import { WEEK } from "constants/common";

import {
  Container,
  InfoContainer,
  Section,
  Text,
  TextWrapper,
  Title,
  TitleContainer,
  TitleAdornment,
  Workout,
  Loader,
  NoInternetConnectionErrorContainer,
} from "./styles";
import { EVENT_TYPE, logEvent } from "utils/analytics";
import { zonedTimeToUTC } from "utils/timezones";
import { useTranslation } from "react-i18next";

type Props = {
  workoutId: number | null;
  movementId: number | null;
  categoryId?: number | null;
  workoutGroup?: string | null;
  workoutInWorkoutCategoryId?: number;
  fromPopup?: boolean;
  onClose?: () => void;
  url?: string;
  history?: any;
};

const WorkoutMovement: React.FC<Props> = observer((props) => {
  const {
    workoutId,
    movementId,
    categoryId,
    workoutGroup,
    workoutInWorkoutCategoryId,
    fromPopup = false,
    onClose,
    url,
    history,
  } = props;

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isNoInternetConnectionError, setIsNoInternetConnectionError] = useState(false);

  const dayOfWeek = React.useMemo(() => {
    if (!workoutGroup) {
      return undefined;
    }

    const day = Object.keys(WEEK).find(key => WEEK[key] === workoutGroup);
    const parsedDay = day !== undefined ? Number.parseInt(day) : undefined;

    return typeof parsedDay === "number" ? parsedDay + 1 : undefined;
  }, [workoutGroup]);

  const details = React.useMemo(() => {
    return movementId
      ? WorkoutDetailsStore.getMovementDetails(movementId)
      : undefined;
  }, [movementId]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);

      try {
        categoryId && (await WorkoutStore.fetchDetails(categoryId));
        const params: any = {
          categoryId: categoryId,
          workoutId: workoutId,
          dayOfWeek: dayOfWeek,
          workoutInWorkoutCategoryId: workoutInWorkoutCategoryId,
        };

        await WorkoutDetailsStore.fetch(params);
      } catch (error) {
        if (cancelled) return;
        
        if (error instanceof NoInternetConnectionError) {
          setIsNoInternetConnectionError(true);
        }
        if (error instanceof NotFoundError && url && history) {
          redirectToRootRoute(history, url);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [categoryId, workoutId, dayOfWeek, workoutInWorkoutCategoryId, url, history]);

  if (isNoInternetConnectionError) {
    return (
      <NoInternetConnectionErrorContainer>
        <NoInternetConnectionPlaceholder
          type={fromPopup ? "popup" : "element"}
          onSubmit={onClose}
        />
      </NoInternetConnectionErrorContainer>
    );
  }

  if (isLoading) {
    return (
      <Loader>
        <Spinner large></Spinner>
      </Loader>
    );
  }

  return (
    <Container>
      <Workout>
        {details?.videoUrl && (
          <Video
            logEvent={() =>
              logEvent({
                event: EVENT_TYPE.PLAY_VIDEO,
                data: {
                  id: details?.id,
                  title: details?.title,
                  date: zonedTimeToUTC(new Date())?.toISOString(),
                },
              })
            }
            imageUrl={details.imageUrl ?? ""}
            video={details.videoUrl}
            duration={details.duration}
          />
        )}
      </Workout>

      <InfoContainer>
        <Section>
          <TitleContainer>
            <TitleAdornment />
            <Title>{t("workouts.workout-details.instructions")}</Title>
          </TitleContainer>
          <TextWrapper>
            <Text>{details?.description}</Text>
          </TextWrapper>
        </Section>
      </InfoContainer>
    </Container>
  );
});

export default WorkoutMovement;
